<template>
  <LModal
    :visible="modelValue"
    :title="t('components.taskboard.moveIssueModal.title')"
    ok-variant="success"
    size="lg"
    :submit="
      async () => {
        await save();
        return { success: true };
      }
    "
    @update:visible="$emit('update:modelValue', $event)"
  >
    <div>
      <label>
        {{ t("components.taskboard.moveIssueModal.buttonLabel") }}
      </label>
      <br />
      <div class="mt-2 mb-3">
        <span class="p-buttonset">
          <template v-for="button in options" :key="button.value">
            <LISAButton
              v-tooltip.left="{
                value: t('components.taskboard.moveIssueModal.escalateButtonTooltip'),
                disabled: !isRootBoard,
              }"
              :data-testid="button.id"
              class="w-50"
              :class="selected === button.value ? '' : 'p-button-outlined'"
              :disabled="isRootBoard && button.value.includes('escalate')"
              @click="selected = button.value"
            >
              <div class="text-center w-100">
                {{ button.text }}
              </div>
            </LISAButton>
          </template>
        </span>
      </div>
    </div>
    <InputRow
      v-if="selected === 'delegate'"
      :model-value="selectedBoardId"
      name="selectedBoardId"
      data-testid="boardSelectDropdown"
      required
      :display-name="t('components.taskboard.moveIssueModal.selectLabel')"
    >
      <template #default="{ field: inputField }">
        <SQDCBoardSelect
          v-bind="inputField"
          v-model="selectedBoardId"
          :variables="{
            siteIds: showAllSites ? undefined : board?.siteId,
          }"
          :disabled-nodes="excludedBoardsFromSearch"
        />
      </template>
      <template #append>
        <div class="text-truncate pl-2">
          <Checkbox v-model="showAllSites" input-id="acrossLocations" class="mr-2" :binary="true" />
          <label
            for="acrossLocations"
            :text="t('components.taskboard.moveIssueModal.acrossSites')"
            >{{ t("components.taskboard.moveIssueModal.acrossSites") }}</label
          >
        </div>
      </template>
    </InputRow>
    <InputRow
      v-else
      name="escalateBoard"
      :display-name="t('components.taskboard.moveIssueModal.selectLabel')"
    >
      <SQDCBoardSelect :model-value="board?.parentId" disabled />
    </InputRow>

    <InputRow
      v-model="reason"
      name="delegateReason"
      :required="systemSettings?.delegateEscalateRequired"
      :rules="[maxLength(140)]"
      :display-name="t('components.taskboard.moveIssueModal.reason')"
    >
      <template #default="{ field: inputField }">
        <PrimeVueTextarea
          v-bind="inputField"
          v-model="reason"
          class="w-100"
          data-testid="moveEventModalDescriptionField"
          name="delegateReason"
        />
      </template>
    </InputRow>
  </LModal>
</template>
<script setup lang="ts">
import { maxLength } from "@/modules/formValidation";
import { computed, ref, type PropType, onMounted, inject, nextTick, watch } from "vue";
import { useTranslation } from "@/language/translation";
import LISAButton from "@LISAPrime/LISAButton.vue";
import PrimeVueTextarea from "primevue/textarea";
import vTooltip from "primevue/tooltip";
import {
  type EventListEventFieldsFragment,
  EventType,
  MoveEventType,
  useMoveEventMutation,
} from "@/graphql";
import {
  BoardIdInjectionKey,
  EABSystemSettingsInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import notEmpty from "@/functions/notEmpty";
import LModal from "@/components/LModal.vue";
import InputRow from "@/components/shared/form/InputRow.vue";
import Checkbox from "primevue/checkbox";
import { useSpecifiedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import SQDCBoardSelect from "@/modules/sqdc/components/selects/SQDCBoardSelect.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  event: {
    type: Object as PropType<EventListEventFieldsFragment>,
    required: true,
  },
});
const emit = defineEmits({
  "update:modelValue": (val: boolean) => val,
});
const { t } = useTranslation();
const selected = ref<MoveEventType>(MoveEventType.escalate);
const selectedBoardId = ref<number | undefined>(undefined);
const reason = ref("");
const systemSettings = inject(EABSystemSettingsInjectionKey);
const showAllSites = ref(false);

const { mutate: moveEvent } = useMoveEventMutation(() => ({
  variables: {
    boardId: selectedBoardId.value!,
    type: selected.value,
    id: props.event.id,
    eventType: props.event.__typename as EventType,
    reason: reason.value,
  },
}));

const boardIdCreatedBoard = computed(
  () => props.event.boardConnection.find((con) => con.status === 0)?.boardId,
);

const boardIdInjection = inject(BoardIdInjectionKey, ref<number | null>(null));

const boardId = computed(() =>
  props.event.boardConnection.some((con) => con.boardId === boardIdInjection.value)
    ? (boardIdInjection.value ?? undefined)
    : boardIdCreatedBoard.value,
);

const { board: boardUsed, boardLoading } = useSpecifiedBoardContext(computed(() => boardId.value));
const board = computed(() => boardUsed.value);
async function setDefault() {
  await nextTick();
  if (isRootBoard.value) {
    selected.value = MoveEventType.delegate;
  } else {
    selected.value = MoveEventType.escalate;
  }
  selectedBoardId.value = board.value?.parentId;

  reason.value = "";
}

watch(boardLoading, (val) => {
  if (!val) {
    setDefault();
  }
});
const isRootBoard = computed(() => !board.value?.path.includes("."));

const options = computed(() => {
  return [
    {
      text: t("components.taskboard.moveIssueModal.escalate"),
      value: MoveEventType.escalate,
      id: "escalate-button",
    },
    {
      text: t("components.taskboard.moveIssueModal.delegate"),
      value: MoveEventType.delegate,
      id: "delegate-button",
    },
  ];
});

const hide = () => {
  emit("update:modelValue", false);
};

const save = async () => {
  moveEvent();
  hide();
};

const excludedBoardsFromSearch = computed(() =>
  [boardId.value].filter(notEmpty).map((id) => ({ key: id, includeChildren: false })),
);

onMounted(async () => {
  setDefault();
});
</script>
